import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { useAuth } from "@/context/AuthContext";

// Útistandandi skuld Details Component
export const UtistandandiSkuldDetails = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalLoaned, setTotalLoaned] = useState(0);
  const [outstandingDebt, setOutstandingDebt] = useState(0);
  const [totalInterestPaid, setTotalInterestPaid] = useState(0);

  useEffect(() => {
    fetchDetails();
  }, []);

  const fetchDetails = async () => {
    try {
      const loansResponse = await axios.get(`${API}/loans`);
      const loans = loansResponse.data;
      setApplications(loans);
      
      // Calculate totals
      let loaned = 0;
      let outstanding = 0;
      let interestPaid = 0;

      loans.forEach(loan => {
        if (loan.status === 'approved' || loan.status === 'disbursed') {
          // Total loaned (original loan amount)
          loaned += loan.loan_amount || 0;
          
          // Use remaining_balance field (calculated from payment schedule)
          outstanding += loan.remaining_balance || 0;
          
          // Calculate total interest paid from payment schedule
          if (loan.payment_schedule && Array.isArray(loan.payment_schedule)) {
            const scheduledPaymentsStatus = loan.scheduled_payments_status || {};
            loan.payment_schedule.forEach(payment => {
              const monthStatus = scheduledPaymentsStatus[String(payment.month)] || '';
              if (monthStatus === 'paid' || payment.paid) {
                interestPaid += payment.interest || 0;
              }
            });
          }
        }
      });

      setTotalLoaned(Math.round(loaned));
      setOutstandingDebt(Math.round(outstanding));
      setTotalInterestPaid(Math.round(interestPaid));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Ekki tókst að sækja upplýsingar');
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8">Hleð upplýsingar...</div>;
  }

  return (
    <div className="p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          {!embedded && (
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Til baka
            </Button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Útistandandi Skuld - Yfirlit</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Samtal lánað</p>
              <p className="text-2xl font-bold text-blue-600">{formatIcelandicNumber(totalLoaned)} kr.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Útistandandi skuld</p>
              <p className="text-2xl font-bold text-purple-600">{formatIcelandicNumber(outstandingDebt)} kr.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Samtals vextir greiddir</p>
              <p className="text-2xl font-bold text-green-600">{formatIcelandicNumber(totalInterestPaid)} kr.</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-4">
            <h2 className="text-lg font-bold mb-3">Virk lán</h2>
            <div className="space-y-2">
              {applications.filter(loan => loan.status === 'approved' || loan.status === 'disbursed').map((loan) => {
                const remaining = loan.remaining_balance || 0;
                
                return (
                  <div key={loan.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/greidslur/${loan.id}`)}
                    data-testid={`debt-loan-${loan.id}`}
                  >
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {loan.borrower_info?.full_name || loan.borrower_info?.name || 'Óþekktur lántaki'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Kennitala: {loan.borrower_info?.ssn || loan.borrower_info?.kennitala || 'N/A'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-600">Lánsfjárhæð: {formatIcelandicNumber(loan.loan_amount || 0)} kr.</p>
                      <p className="text-sm font-bold text-purple-600">Eftirstöðvar: {formatIcelandicNumber(remaining)} kr.</p>
                    </div>
                  </div>
                );
              })}
              
              {applications.filter(loan => loan.status === 'approved' || loan.status === 'disbursed').length === 0 && (
                <p className="text-center text-gray-500 py-4">Engin virk lán fundust</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


